import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { generateAISuggestions } from '../../../services/aiConnectionEngine';

const { FiZap, FiChevronDown, FiChevronUp, FiX, FiUserPlus, FiInfo } = FiIcons;

const CONF_COLORS = {
  high:   'text-emerald-700 bg-emerald-50 border-emerald-200',
  medium: 'text-amber-700 bg-amber-50 border-amber-200',
  low:    'text-gray-600 bg-gray-50 border-gray-200',
};

export default function AITreeSuggestions({ treeNodes = [], memorial, onSuggestMember }) {
  const [open, setOpen]         = useState(false);
  const [dismissed, setDismissed] = useState(new Set());

  // Build pseudo-members from the tree nodes to run through the engine
  const pseudoMembers = useMemo(() =>
    treeNodes.map(n => ({
      id: `tree-${n.id}`,
      full_name: n.name,
      relationship: n.relation,
      birth_year: n.birthYear,
      is_deceased: n.isDeceased ? 1 : 0,
      death_year: n.deathYear,
      bio: n.bio || '',
      linked_memorial_id: memorial?.id || '',
      linked_memorial_name: memorial?.name || '',
    })),
  [treeNodes, memorial]);

  const suggestions = useMemo(() => {
    if (pseudoMembers.length < 2) return [];
    return generateAISuggestions(pseudoMembers)
      .filter(s => s.type === 'member_pair' && s.confidenceScore >= 50)
      .slice(0, 6);
  }, [pseudoMembers]);

  const visible = suggestions.filter(s => !dismissed.has(s.id));

  if (visible.length === 0) return null;

  return (
    <div className="bg-white rounded-[28px] border border-brand-dark shadow-sm overflow-hidden">
      {/* Toggle Header */}
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-brand-light/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <SafeIcon icon={FiZap} className="w-4 h-4 text-white" />
          </div>
          <div className="text-left">
            <p className="text-sm font-serif text-accent-navy">AI Relationship Insights</p>
            <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">
              {visible.length} possible connection{visible.length !== 1 ? 's' : ''} detected
            </p>
          </div>
        </div>
        <SafeIcon icon={open ? FiChevronUp : FiChevronDown} className="w-4 h-4 text-text-muted" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-brand-dark"
          >
            <div className="p-4 space-y-3">
              <div className="flex items-start gap-2 p-3 bg-violet-50 rounded-xl border border-violet-100">
                <SafeIcon icon={FiInfo} className="w-3.5 h-3.5 text-violet-600 flex-shrink-0 mt-0.5" />
                <p className="text-[9px] text-violet-800 leading-relaxed">
                  AI detected these potential family connections based on shared surnames, birth years, and biographical details within this tree.
                </p>
              </div>

              <AnimatePresence mode="popLayout">
                {visible.map(s => {
                  const confStyle = CONF_COLORS[s.confidenceLabel] || CONF_COLORS.low;
                  return (
                    <motion.div
                      key={s.id}
                      layout
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: 40 }}
                      className={`rounded-2xl border p-3 ${confStyle}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-black truncate">
                            {s.sourceMemberName} ↔ {s.suggestedName}
                          </p>
                          <p className="text-[10px] mt-0.5 leading-snug opacity-80">{s.reason}</p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {(s.signals || []).slice(0, 3).map((sig, i) => (
                              <span key={i} className="text-[8px] font-bold px-2 py-0.5 rounded-full bg-white/70 border border-current">
                                {sig}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <span className="text-[9px] font-black">{s.confidenceScore}%</span>
                          {onSuggestMember && (
                            <button
                              onClick={() => onSuggestMember(s)}
                              className="p-1.5 rounded-lg bg-white/70 hover:bg-white transition-colors"
                              title="Add to family network"
                            >
                              <SafeIcon icon={FiUserPlus} className="w-3 h-3" />
                            </button>
                          )}
                          <button
                            onClick={() => setDismissed(prev => new Set([...prev, s.id]))}
                            className="p-1.5 rounded-lg hover:bg-white/70 transition-colors opacity-60 hover:opacity-100"
                          >
                            <SafeIcon icon={FiX} className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}